import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { EditorState } from 'draft-js';
import { richTextToHtml, richTextContentFromHtml } from '../../shared/utils/richTextHtml';

import RichEditor from '../../RichEditor/components/RichEditor';

const _ = require('lodash');

// Standalone editor for the V3Offer `additional_terms` HTML field. Runs its
// own debounced PATCH so it stays decoupled from the deal-terms pane's
// autosave loop — only the additional_terms field is sent.
//
// When `embedded` is true the outer card chrome (border/heading) is dropped —
// callers that mount this inside their own card chrome (e.g. the
// V3OfferAdditionalTermsCard accordion) should use this mode.
const V3OfferAdditionalTermsSection = ({ team, csrfToken, offer, embedded, onSaved }) => {
  const [editorState, setEditorState] = useState(() => (
    offer?.additional_terms
      ? EditorState.createWithContent(richTextContentFromHtml(offer.additional_terms))
      : EditorState.createEmpty()
  ));

  // idle | saving | saved | error — drives the autosave confirmation shown
  // above the editor so it's obvious notes are captured automatically.
  const [saveStatus, setSaveStatus] = useState('idle');

  // Keep onSaved in a ref so the debounced saver isn't rebuilt when the parent
  // passes a fresh callback each render.
  const onSavedRef = useRef(onSaved);
  useEffect(() => { onSavedRef.current = onSaved; }, [onSaved]);

  // Guard setState after unmount — the flush-on-unmount PATCH below resolves
  // after this component is gone (the parent stays mounted to receive onSaved).
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Re-seed when the active offer changes (e.g. the user picked another
  // lineup card and the page rebound `offer`).
  const lastSeededOfferId = useRef(offer?.id || null);
  useEffect(() => {
    if (offer?.id === lastSeededOfferId.current) return;
    lastSeededOfferId.current = offer?.id || null;
    setSaveStatus('idle');
    setEditorState(
      offer?.additional_terms
        ? EditorState.createWithContent(richTextContentFromHtml(offer.additional_terms))
        : EditorState.createEmpty(),
    );
  }, [offer?.id, offer?.additional_terms]);

  const debouncedSave = useMemo(
    () => _.debounce((offerId, html) => {
      if (!offerId) return;
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      axios.patch(`/teams/${team.id}/offers/v3/${offerId}`, {
        v3_offer: { additional_terms: html },
      })
        .then(() => {
          // Keep the parent's offer state in sync at save cadence so re-mounting the
          // editor (e.g. switching tabs and back) seeds from the latest note rather
          // than the stale value the offer was loaded with.
          onSavedRef.current(html);
          if (mountedRef.current) setSaveStatus('saved');
        })
        .catch(() => { if (mountedRef.current) setSaveStatus('error'); });
    }, 600),
    [team.id, csrfToken],
  );

  // Flush (not cancel) on unmount so a pending edit is persisted when the user
  // switches tabs before the debounce fires.
  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  const handleEditorStateChange = (nextState) => {
    // draft-js fires this for selection changes too; only treat actual content
    // edits as a save (and surface the "Saving…" status).
    const contentChanged = nextState.getCurrentContent() !== editorState.getCurrentContent();
    setEditorState(nextState);
    if (!offer?.id || !contentChanged) return;
    setSaveStatus('saving');
    const html = richTextToHtml(nextState.getCurrentContent());
    debouncedSave(offer.id, html);
  };

  const renderSaveStatus = () => {
    if (saveStatus === 'saving') {
      return <span className="v3-additional-terms-card__save">Saving…</span>;
    }
    if (saveStatus === 'saved') {
      return (
        <span className="v3-additional-terms-card__save v3-additional-terms-card__save--saved">
          <i className="far fa-check" aria-hidden="true" /> Saved
        </span>
      );
    }
    if (saveStatus === 'error') {
      return (
        <span className="v3-additional-terms-card__save v3-additional-terms-card__save--error">
          Couldn’t save — keep typing to retry
        </span>
      );
    }
    return null;
  };

  const editor = (
    <>
      <div
        className="v3-additional-terms-card__save-row"
        aria-live="polite"
        data-testid="notes-save-status"
      >
        {renderSaveStatus()}
      </div>
      <div className="v3-additional-terms-card__editor">
        <RichEditor
          wrapperClassName="border-0"
          editorState={editorState}
          onEditorStateChange={handleEditorStateChange}
        />
      </div>
    </>
  );

  if (embedded) {
    // Caller (accordion card) supplies the heading + outer chrome.
    return editor;
  }

  return (
    <div className="v3-additional-terms-card mb-4">
      <span className="v3-additional-terms-card__heading">Additional terms</span>
      {editor}
    </div>
  );
};

V3OfferAdditionalTermsSection.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  csrfToken: PropTypes.string.isRequired,
  offer: PropTypes.shape({
    id: PropTypes.number,
    additional_terms: PropTypes.string,
  }),
  embedded: PropTypes.bool,
  onSaved: PropTypes.func,
};

V3OfferAdditionalTermsSection.defaultProps = {
  offer: null,
  embedded: false,
  onSaved: () => {},
};

export default V3OfferAdditionalTermsSection;
